<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import type { ChatMessage } from "@/api/types";

const props = defineProps<{ message: ChatMessage }>();
const emit = defineEmits<{ (e: "approve", id: string, decision: "approved" | "rejected"): void }>();
const { t } = useI18n();

const open = ref(false);
const m = props.message;
</script>

<template>
  <div class="msg" :class="`msg--${m.sender_kind}`">
    <!-- plain text -->
    <template v-if="m.kind === 'text'">
      <div class="msg__who">
        {{ m.sender_kind === "operator" ? t("chat.you") : "🤖 " + t("chat.worker") }}
      </div>
      <div class="msg__text">{{ m.content }}</div>
    </template>

    <!-- tool-call trace (collapsible) -->
    <details v-else-if="m.kind === 'trace'" class="msg__trace" :open="open">
      <summary>🔧 {{ m.metadata?.tool?.name || m.content }}</summary>
      <div v-if="m.metadata?.tool?.args" class="msg__pre">
        <b>args:</b> {{ m.metadata.tool.args }}
      </div>
      <pre v-if="m.metadata?.tool?.result" class="msg__pre">{{ m.metadata.tool.result }}</pre>
    </details>

    <!-- approval request: show the EXACT bound command (WYSIWYR), not the
         worker's free text — the token authorizes metadata.command verbatim. -->
    <div v-else-if="m.kind === 'approval_request'" class="msg__approval">
      <div class="msg__who">🤖 {{ t("chat.worker") }} · {{ t("chat.approval_needed") }}</div>
      <div v-if="m.metadata?.agent_id" class="msg__approval-target">
        {{ t("chat.approval_on") }} <code>{{ m.metadata.agent_id }}</code>
      </div>
      <!-- MCP skill approval carries {skill, args}; cmd.exec carries {command}. -->
      <template v-if="m.metadata?.skill">
        <div class="msg__approval-label">{{ t("chat.approval_skill") }}</div>
        <pre class="msg__plan" data-test="approval-skill">{{ m.metadata.skill }}<span v-if="m.metadata.args">  {{ JSON.stringify(m.metadata.args) }}</span></pre>
      </template>
      <template v-else>
        <div class="msg__approval-label">{{ t("chat.approval_command") }}</div>
        <pre class="msg__plan" data-test="approval-command">{{
          m.metadata?.command || m.metadata?.plan || m.content
        }}</pre>
      </template>
      <div v-if="m.metadata?.purpose" class="msg__approval-why">
        {{ t("chat.approval_why") }} {{ m.metadata.purpose }}
      </div>
      <div class="msg__actions">
        <button class="msg__approve" type="button" @click="emit('approve', m.id, 'approved')">
          {{ t("chat.approve") }}
        </button>
        <button class="msg__reject" type="button" @click="emit('approve', m.id, 'rejected')">
          {{ t("chat.reject") }}
        </button>
      </div>
    </div>

    <!-- approval result -->
    <div v-else-if="m.kind === 'approval_result'" class="msg__result">
      {{ m.content === "approved" ? "✅ " + t("chat.approved") : "❌ " + t("chat.rejected") }}
    </div>
  </div>
</template>

<style scoped>
.msg {
  margin: 0.4rem 0;
  max-width: 80%;
}
.msg--operator {
  margin-left: auto;
  text-align: right;
}
.msg__who {
  font-size: 0.72rem;
  color: var(--color-fg-dim);
  margin-bottom: 0.15rem;
}
.msg__text {
  display: inline-block;
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  background: var(--color-bg-elev);
  white-space: pre-wrap;
  text-align: left;
}
.msg--operator .msg__text {
  background: var(--color-accent);
  color: #fff;
}
.msg__trace {
  font-size: 0.8rem;
  color: var(--color-fg-dim);
  border-left: 3px solid var(--color-border);
  padding: 0.15rem 0 0.15rem 0.5rem;
}
.msg__trace summary {
  cursor: pointer;
}
.msg__pre,
.msg__plan {
  margin: 0.3rem 0 0;
  padding: 0.5rem;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 0.78rem;
  white-space: pre-wrap;
  overflow-x: auto;
  text-align: left;
}
.msg__approval {
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--color-accent);
  border-radius: 10px;
  background: var(--color-bg-elev);
  text-align: left;
}
.msg__actions {
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
}
.msg__approve {
  background: var(--color-ok);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.35rem 0.9rem;
  cursor: pointer;
}
.msg__reject {
  background: transparent;
  color: var(--color-fg);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.35rem 0.9rem;
  cursor: pointer;
}
.msg__result {
  font-size: 0.85rem;
  color: var(--color-fg-dim);
}
</style>
